import { useTranslation } from 'react-i18next';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import { DELIVERY_INSTRUCTIONS_FIELDS } from '../../../../shared/fieldConfig/constants';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { VALIDATION } from '../../../../utils/validation';
import type { CardField, CardFieldComponent, CardFieldProps } from '../../types';
import CopyShipTo from '../CopyShipTo';

const category = CARD_CATEGORIES.deliveryInstructions;

function ShippingInstructions({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'shippingInstructions';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function CustomsText({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'customsText';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function externalInvoiceText({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'externalInvoiceText';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function LoadingInstructions({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'loadingInstructions';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function UnloadingInstructions({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'unloadingInstructions';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function PackingInstructions({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'packingInstructions';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}
function BillOfLadingText({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'billOfLadingText';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function TextNotaFiscal({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'textNotaFiscal';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

function ShippingMarks({ isReview, t, fieldPrefix }: CardFieldProps) {
  const field = 'shippingMarks';
  const name = `${fieldPrefix}.${field}`;

  const config = {
    disabled: isReview,
    category,
    field,
    name,
    label: t(`shipTo.${category}.${field}.label`),
    type: CONTROL_TYPE.textarea,
    validations: [{ type: VALIDATION.maxCharacters, param: 3500 }],
    placeholder: t(`shipTo.${category}.${field}.placeholder`)
  };

  return <Control {...config} />;
}

interface DeliveryInstructionsCardProps {
  isReview?: boolean;
  index?: number;
  fields: CardField[];
  fieldWrapper?: string;
}

function DeliveryInstructionsCard({ isReview, index = 0, fields, fieldWrapper }: DeliveryInstructionsCardProps) {
  const { t } = useTranslation();

  const cardProps = {
    cardId: category,
    title: t('shipTo.deliveryInstructions.title'),
    subtitle: t('shipTo.deliveryInstructions.subtitle'),
    image: '/food/onboarding/assets/Instructions.svg',
    isReview,
    fieldWrapper,
    t
  };

  const controlMap: Record<string, CardFieldComponent> = {
    [DELIVERY_INSTRUCTIONS_FIELDS.shippingInstructions]: ShippingInstructions,
    [DELIVERY_INSTRUCTIONS_FIELDS.customsText]: CustomsText,
    [DELIVERY_INSTRUCTIONS_FIELDS.externalInvoiceText]: externalInvoiceText,
    [DELIVERY_INSTRUCTIONS_FIELDS.loadingInstructions]: LoadingInstructions,
    [DELIVERY_INSTRUCTIONS_FIELDS.unloadingInstructions]: UnloadingInstructions,
    [DELIVERY_INSTRUCTIONS_FIELDS.packingInstructions]: PackingInstructions,
    [DELIVERY_INSTRUCTIONS_FIELDS.billOfLadingText]: BillOfLadingText,
    [DELIVERY_INSTRUCTIONS_FIELDS.textNotaFiscal]: TextNotaFiscal,
    [DELIVERY_INSTRUCTIONS_FIELDS.shippingMarks]: ShippingMarks
  };

  let wrapper = '';
  // allows for fields to be included on the root level, nested object, or nested array
  if (fieldWrapper) {
    wrapper = index > -1 ? `${fieldWrapper}[${index}].` : `${fieldWrapper}.`;
  }

  const fieldPrefix = `${wrapper}${category}`;

  return (
    <FormCard {...cardProps}>
      {!isReview && index > -1 && <CopyShipTo index={index} category={category} />}
      {fields.map(({ key, ...fieldProps }: CardField) => {
        const Component = controlMap[key];
        return <Component t={t} isReview={isReview} fieldPrefix={fieldPrefix} {...fieldProps} key={key} />;
      })}
    </FormCard>
  );
}

export default DeliveryInstructionsCard;
